import { useState, useEffect, useCallback, useMemo } from 'react';
import authHeader from 'services/auth/jwt/auth-header';
import customAxios from 'routes/Pages/util/Api';
import { useToast } from 'context/tostifyContext';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';

const defaultCenter = { lat: 53.4494762, lng: -7.5029786 };

const useGeoFencing = ({ isMapLoaded }) => {
  const { showToast } = useToast();

  const [geoFenceData, setGeoFenceData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const [center, setCenter] = useState(defaultCenter);
  const [zoom, setZoom] = useState(8);

  // keep both full and filtered versions
  const [allSiteAssessments, setAllSiteAssessments] = useState([]);
  const [siteAssessments, setSiteAssessments] = useState([]);

  const [allContracts, setAllContracts] = useState([]);
  const [contracts, setContracts] = useState([]);

  const [isEdit, setIsEdit] = useState(false);

  // NEW: Cache for site-contract relationships
  const [siteContractMap, setSiteContractMap] = useState(new Map());
  const [contractSiteMap, setContractSiteMap] = useState(new Map());
  const [loading, setLoading] = useState(false);

  const [geoFenceForm, setGeoFenceForm] = useState({
    id: undefined,
    siteAssessmentID: null,
    contractUniqueID: null,
    locationName: '',
    latitude: null,
    longitude: null,
    radius: '',
  });

  const [pinData, setPinData] = useState([]);
  const [activePin, setActivePin] = useState(null);

  useEffect(() => {
    if (pinData.length > 0) {
      setCenter({
        lat: pinData[0].latitude,
        lng: pinData[0].longitude,
      });
    }
  }, [pinData]);

  useEffect(() => {
    if (!geoFenceForm.siteAssessmentID) return;

    const fetchEntrancePins = async () => {
      try {
        const response = await customAxios.get(
          `/SiteDetails/GetSiteDetailsMapCoordinates/${geoFenceForm.siteAssessmentID}?mapType=pin`,
          { headers: authHeader() },
        );

        if (response.status === 200) {
          const data = Array.isArray(response.data) ? response.data : [response.data];

          // Only keep pins where mapLabel starts with "Entrance"
          const entrancePins = data
            .filter(p => p.mapLabel?.toLowerCase().startsWith('entrance'))
            .map(p => ({
              siteAssessmentID: p.siteAssessmentID,
              latitude: p.latitude,
              longitude: p.longitude,
              label: p.mapLabel, // keep displayName/mapLabel
            }));

          setPinData(entrancePins);
          console.log('Entrance pins updated:', entrancePins);
        }
      } catch (err) {
        console.error('Error fetching entrance pins:', err);
      }
    };

    fetchEntrancePins();
  }, [geoFenceForm.siteAssessmentID]);

  useEffect(() => {
    fetchGeoFencesList();
    fetchSiteAssessments();
    fetchContacts();
  }, []);

  // useEffect(() => {
  //   if (geoFenceForm.latitude && geoFenceForm.longitude) {
  //     setCenter({ lat: geoFenceForm.latitude, lng: geoFenceForm.longitude });
  //     setZoom(14);
  //   } else {
  //     getCurrentUserLocation();
  //   }
  // }, [geoFenceForm, isMapLoaded]);

  // NEW: Build relationship maps when data is loaded
  useEffect(() => {
    if (allSiteAssessments.length > 0 && allContracts.length > 0) {
      buildRelationshipMaps();
    }
  }, [allSiteAssessments, allContracts]);

  const getCurrentUserLocation = () => {
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCenter({ lat: coords.latitude, lng: coords.longitude });
        setZoom(14);
      },
      error => {
        console.warn('Geolocation error:', error);
        geocodeLocation('Ireland');
      },
    );
  };

  const fetchSiteAssessments = async () => {
    try {
      const response = await customAxios.get(`/CRM/GetCRMSiteDetails`, {
        headers: authHeader(),
      });
      setAllSiteAssessments(response.data);
      setSiteAssessments(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const geocodeLocation = (location = 'Pune') => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: location }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setCenter({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  };

  const fetchGeoFencesList = async siteId => {
    try {
      const response = await customAxios.get('/Ops/GetGeofencingData', {
        headers: authHeader(),
      });
      if (response.status === 200) {
        const uniqueGeoFences = response.data.filter((item, index, self) => index === self.findIndex(t => t.id === item.id));

        setGeoFenceData(uniqueGeoFences);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await customAxios.get(`/Ops/GetContracts`, {
        headers: authHeader(),
      });
      setAllContracts(response.data);
      setContracts(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  // NEW: Build relationship maps for instant lookup
  const buildRelationshipMaps = () => {
    const siteToContracts = new Map();
    const contractToSite = new Map();

    // Group contracts by CRM ID
    const contractsByCrmId = new Map();
    allContracts.forEach(contract => {
      if (!contractsByCrmId.has(contract.crmID)) {
        contractsByCrmId.set(contract.crmID, []);
      }
      contractsByCrmId.get(contract.crmID).push(contract);
    });

    // Map sites to contracts
    allSiteAssessments.forEach(site => {
      const relatedContracts = contractsByCrmId.get(site.crmID) || [];
      siteToContracts.set(site.siteAssessmentID, relatedContracts);

      // Map each contract back to this site
      relatedContracts.forEach(contract => {
        contractToSite.set(contract.contractUniqueID, site);
      });
    });

    setSiteContractMap(siteToContracts);
    setContractSiteMap(contractToSite);
  };

  // NEW: Get contracts for a site instantly from cache
  const getContractsForSite = siteAssessmentID => {
    return siteContractMap.get(siteAssessmentID) || [];
  };

  // NEW: Get site for a contract instantly from cache
  const getSiteForContract = contractUniqueID => {
    return contractSiteMap.get(contractUniqueID) || null;
  };

  // When contract is chosen → get matching site (EXISTING LOGIC PRESERVED)
  const fetchSiteForContract = async contractUniqueID => {
    if (!contractUniqueID) {
      // cleared → reset full list
      setSiteAssessments(allSiteAssessments);
      return;
    }

    try {
      const res = await customAxios.get(`/Ops/GetContracts?contractUniqueID=${contractUniqueID}`, {
        headers: authHeader(),
      });

      const siteRes = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${res.data[0].siteAssessmentID}`, {
        headers: authHeader(),
      });

      setSiteAssessments([siteRes.data[0]]);

      // Update form with the site assessment ID
      setGeoFenceForm(prev => ({
        ...prev,
        siteAssessmentID: res.data[0].siteAssessmentID,
      }));
    } catch (error) {
      console.error('Error fetching site for contract:', error);
    }
  };

  // When site is chosen → get contracts (EXISTING LOGIC PRESERVED)
  const fetchContractsForSite = async siteAssessmentID => {
    if (!siteAssessmentID) {
      // cleared → reset full list
      setContracts(allContracts);
      setGeoFenceForm(prev => ({
        ...prev,
        contractUniqueID: null, // Clear contract selection
      }));
      return;
    }

    try {
      const siteRes = await customAxios.get(`/CRM/GetCRMSiteDetails?siteAssessmentId=${siteAssessmentID}`, {
        headers: authHeader(),
      });

      const contractsRes = await customAxios.get(`/Ops/GetContracts?crmID=${siteRes.data[0].crmID}`, {
        headers: authHeader(),
      });

      // Set the filtered contracts
      setContracts(contractsRes.data);

      // Auto-select the first contract if available
      const firstContract = contractsRes.data.length > 0 ? contractsRes.data[0] : null;

      setGeoFenceForm(prev => ({
        ...prev,
        siteAssessmentID: siteAssessmentID,
        contractUniqueID: firstContract?.contractUniqueID ?? null, // Auto-select first contract
      }));
    } catch (error) {
      console.error('Error fetching contracts for site:', error);
      // Reset contracts on error
      setContracts(allContracts);
      setGeoFenceForm(prev => ({
        ...prev,
        contractUniqueID: null,
      }));
    }
  };

  const resetContractsToAll = () => {
    setContracts(allContracts);
  };

  const resetSiteAssessmentsToAll = () => {
    setSiteAssessments(allSiteAssessments);
  };

  // EXISTING Combined functions for better UX
  const handleSiteChange = async siteAssessmentID => {
    updateGeoFenceForm('contractUniqueID', null);
    updateGeoFenceForm('siteAssessmentID', siteAssessmentID);

    if (siteAssessmentID) {
      await fetchContractsForSite(siteAssessmentID);
    } else {
      resetContractsToAll();
    }
  };

  const handleContractChange = async contractUniqueID => {
    updateGeoFenceForm('contractUniqueID', contractUniqueID);

    if (contractUniqueID) {
      await fetchSiteForContract(contractUniqueID);
    } else {
      resetSiteAssessmentsToAll();
    }
  };

  // NEW: Instant handlers using cache (alternative to existing async handlers)
  const handleSiteChangeInstant = siteAssessmentID => {
    updateGeoFenceForm('contractUniqueID', null);
    updateGeoFenceForm('siteAssessmentID', siteAssessmentID);

    if (siteAssessmentID) {
      // Get contracts instantly from cache
      const relatedContracts = getContractsForSite(siteAssessmentID);
      setContracts(relatedContracts);

      // Auto-select first contract
      const firstContract = relatedContracts.length > 0 ? relatedContracts[0] : null;
      if (firstContract) {
        updateGeoFenceForm('contractUniqueID', firstContract.contractUniqueID);
      }
    } else {
      setContracts(allContracts);
    }
  };

  const handleContractChangeInstant = contractUniqueID => {
    updateGeoFenceForm('contractUniqueID', contractUniqueID);

    if (contractUniqueID) {
      // Get site instantly from cache
      const relatedSite = getSiteForContract(contractUniqueID);
      if (relatedSite) {
        setSiteAssessments([relatedSite]);
        updateGeoFenceForm('siteAssessmentID', relatedSite.siteAssessmentID);
      }
    } else {
      setSiteAssessments(allSiteAssessments);
      updateGeoFenceForm('siteAssessmentID', null);
    }
  };

  const handleMapClick = useCallback(e => {
    setGeoFenceForm(prev => ({
      ...prev,
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
    }));
  }, []);

  // REVERTED: Back to original functionality - don't pre-load data
  const openGeoFenceModal = () => {
    setIsOpen(true);
  };

  const closeGeoFenceModal = () => {
    setIsOpen(false);
    setIsEdit(false);
    resetForm();
    setSiteAssessments(allSiteAssessments);
    setContracts(allContracts);
    setPinData([]);
    setActivePin(null);
  };

  const resetForm = () => {
    setGeoFenceForm({
      id: undefined,
      siteAssessmentID: null,
      contractUniqueID: null,
      locationName: '',
      latitude: null,
      longitude: null,
      radius: '',
    });
  };

  const handleEdit = props => {
    setGeoFenceForm({
      id: props.id,
      siteAssessmentID: props.siteAssessmentID ?? null,
      contractUniqueID: props.contractUniqueID ?? null,
      locationName: props.locationName ?? '',
      latitude: props.latitude ?? null,
      longitude: props.longitude ?? null,
      radius: props.radius?.toString() ?? '',
      geofenceType: props.geofenceType,
    });
    setIsEdit(true);
    setIsOpen(true);
  };

  const handleSave = async () => {
    let payload;
    let url;

    if (!geoFenceForm.geofenceType || geoFenceForm.geofenceType === '') {
      showToast('Geofence Type cannot be empty. Kindly select geofence type', 'error');
    }

    if (isEdit) {
      url = `/Ops/EditGeofencingData`;
      payload = {
        id: geoFenceForm.id,
        siteAssessmentID: geoFenceForm.siteAssessmentID ?? null,
        contractUniqueID: geoFenceForm.contractUniqueID ?? null,
        locationName: geoFenceForm.locationName,
        latitude: geoFenceForm.latitude,
        longitude: geoFenceForm.longitude,
        radius: parseFloat(geoFenceForm.radius),
        mapType: 'pin',
        geofenceType: geoFenceForm.geofenceType,
      };
    } else {
      url = `/Ops/AddGeofencingData`;
      payload = {
        siteAssessmentID: geoFenceForm.siteAssessmentID ?? null,
        contractUniqueID: geoFenceForm.contractUniqueID ?? null,
        locationName: geoFenceForm.locationName,
        latitude: geoFenceForm.latitude,
        longitude: geoFenceForm.longitude,
        radius: parseFloat(geoFenceForm.radius),
        mapType: 'pin',
        geofenceType: geoFenceForm.geofenceType,
      };
    }
    console.log('Saving GeoFence:', payload, isEdit);

    try {
      const response = await customAxios.post(url, payload, {
        headers: authHeader(),
      });

      if (response.status === 200) {
        isEdit ? showToast('Geofence updated successfully', 'success') : showToast('Geofence added successfully', 'success');
        fetchGeoFencesList();

        // updated record modified date
        await updateRecordModifiedDate(payload.siteAssessmentID);
      }
    } catch (error) {
      console.error('Failed to save geofence:', error);
      showToast('Failed to save geofence', 'error');
    } finally {
      closeGeoFenceModal();
    }
  };

  const handleDelete = async id => {
    if (window.confirm('Are you sure you want to delete this GeoFence?')) {
      try {
        const response = await customAxios.post(
          '/Ops/DeleteGeofencingData',
          { id },
          {
            headers: authHeader(),
          },
        );
        if (response.status === 200) {
          showToast('Geofence deleted successfully', 'success');
          fetchGeoFencesList();
        }
      } catch (error) {
        console.log(error);
        showToast('Failed to delete geofence', 'error');
      }
    }
  };

  const updateGeoFenceForm = (field, value) => {
    console.log('FIELD ====', value);
    setGeoFenceForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const memoizedSiteOptions = useMemo(() => siteAssessments, [siteAssessments]);
  const memoizedContractOptions = useMemo(() => contracts, [contracts]);

  return {
    geoFenceData,
    isOpen,
    openGeoFenceModal,
    closeGeoFenceModal,
    geoFenceForm,
    updateGeoFenceForm,
    handleMapClick,
    handleSave,
    siteAssessments: memoizedSiteOptions,
    contracts: memoizedContractOptions,
    center,
    zoom,
    handleDelete,
    handleEdit,
    fetchSiteForContract,
    fetchContractsForSite,
    resetContractsToAll,
    resetSiteAssessmentsToAll,
    handleSiteChange,
    handleContractChange,
    loading, // NEW: Loading state
    // NEW: Additional functions for instant operations
    getContractsForSite,
    getSiteForContract,
    handleSiteChangeInstant,
    handleContractChangeInstant,
    pinData,
    activePin,
    setActivePin,
  };
};

export default useGeoFencing;
